import React, { useState, useEffect } from 'react';
import { FaTruck, FaSearch, FaFilter, FaEye, FaEdit, FaTrash, FaPlus, FaSpinner, FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { jobService } from '../../api/jobService';
import JobFormModal from '../../components/admin/JobFormModal';
import JobDetailsModal from '../../components/admin/JobDetailsModal';
import AssignJobModal from '../../components/admin/AssignJobModal';
import DeleteJobModal from '../../components/admin/DeleteJobModal';

const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [deletingJob, setDeletingJob] = useState(null);
  const [assigningJob, setAssigningJob] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);

  useEffect(() => {
    fetchJobs();
    fetchAvailableDrivers();
  }, [pagination.page, statusFilter, categoryFilter, priorityFilter]);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchJobs();
      } else if (searchTerm === '') {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchJobs();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(categoryFilter !== 'all' && { category: categoryFilter }),
        ...(priorityFilter !== 'all' && { priority: priorityFilter }),
        ...(searchTerm && { search: searchTerm })
      };

      const response = await jobService.getAllJobs(params);
      
      if (response.success) {
        setJobs(response.data.jobs);
        setPagination(response.data.pagination);
      } else {
        toast.error(response.message || 'Failed to load jobs');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDrivers = async () => {
    try {
      console.log('Fetching available drivers');
      const response = await jobService.getAvailableDrivers();
      if (response.success) {
        setAvailableDrivers(response.data.drivers || []);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleDeleteJob = (job) => {
    setSelectedJob(job);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (jobId) => {
    try {
      setDeletingJob(jobId);
      const response = await jobService.deleteJob(jobId);
      
      if (response.success) {
        setJobs(prev => prev.filter(job => job.id !== jobId));
        toast.success('Job deleted successfully');
        setShowDeleteModal(false);
        setSelectedJob(null);
      } else {
        toast.error(response.message || 'Failed to delete job');
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job. Please try again.');
    } finally {
      setDeletingJob(null);
    }
  };

  const handleBulkDelete = async (jobIds) => {
    try {
      setDeletingJob('bulk');
      const deletePromises = jobIds.map(jobId => jobService.deleteJob(jobId));
      const results = await Promise.allSettled(deletePromises);
      
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.success).length;
      const failed = results.length - successful;
      
      if (successful > 0) {
        setJobs(prev => prev.filter(job => !jobIds.includes(job.id)));
        toast.success(`Successfully deleted ${successful} jobs`);
      }
      
      if (failed > 0) {
        toast.error(`Failed to delete ${failed} jobs`);
      }
    } catch (error) {
      console.error('Error in bulk delete:', error);
      toast.error('Bulk delete operation failed');
    } finally {
      setDeletingJob(null);
    }
  };

  const handleAssignJob = async (jobId, driverId) => {
    try {
      setAssigningJob(jobId);
      const response = await jobService.assignJob(jobId, driverId);
      
      if (response.success) {
        // Find the job and driver details for the success message
        const job = jobs.find(j => j.id === jobId);
        const driver = availableDrivers.find(d => d.id === driverId);
        
        // Update jobs state
        setJobs(prev => prev.map(job => 
          job.id === jobId ? { ...job, status: 'ASSIGNED', driverId } : job
        ));
        
        // Show detailed success message
        const driverName = driver ? `${driver.firstName} ${driver.lastName}` : 'Driver';
        const jobRoute = job?.route || `${job?.pickupLocation} → ${job?.deliveryLocation}`;
        
        toast.success(
          `✅ Job successfully assigned to ${driverName}! 
          Route: ${jobRoute}`,
          {
            autoClose: 5000,
            position: "top-right",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        
        // Refresh available drivers list since this driver is no longer available
        fetchAvailableDrivers();
        
      } else {
        toast.error(response.message || 'Failed to assign job');
      }
    } catch (error) {
      console.error('Error assigning job:', error);
      toast.error('Failed to assign job. Please try again.');
    } finally {
      setAssigningJob(null);
    }
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    setShowDetailModal(true);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setShowEditModal(true);
  };

  const handleCreateJob = () => {
    setSelectedJob(null);
    setShowCreateModal(true);
  };

  const handleOpenAssignModal = (job) => {
    setSelectedJob(job);
    setShowAssignModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
      case 'ASSIGNED':
        return 'bg-blue-100 text-blue-800';
      case 'AVAILABLE':
        return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-100 text-red-800';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = (progress) => {
    switch (progress) {
      case 'DELIVERED':
        return 'bg-green-100 text-green-800';
      case 'PICKED_UP':
        return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'AVAILABLE':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Job Management</h1>
            <p className="text-gray-600">Monitor and manage delivery jobs</p>
          </div>
          <button
            onClick={handleCreateJob}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Create Job
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search jobs by title or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="AVAILABLE">Available</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
        <div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Categories</option>
            <option value="EXPRESS">Express</option>
            <option value="STANDARD">Standard</option>
            <option value="BULK">Bulk</option>
            <option value="FRAGILE">Fragile</option>
            <option value="FOOD">Food</option>
            <option value="DOCUMENTS">Documents</option>
          </select>
        </div>
        <div>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Priorities</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="URGENT">Urgent</option>
          </select>
        </div>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setCategoryFilter('all');
              setPriorityFilter('all');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
          
      
        </div>
        <div className="text-sm text-gray-600">
          Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
          {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
          {pagination.total} jobs
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <FaSpinner className="animate-spin h-8 w-8 text-blue-500" />
            <span className="ml-2 text-gray-600">Loading jobs...</span>
          </div>
        )}
        {deletingJob === 'bulk' && (
          <div className="flex items-center justify-center py-4 bg-red-50 border border-red-200">
            <FaSpinner className="animate-spin h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">Deleting jobs...</span>
          </div>
        )}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Locations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status & Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Driver & Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {job.title || `Job #${job.id}`}
                        </div>
                        <div className="text-sm text-gray-500">
                          {job.category || 'Standard'} • {job.description?.substring(0, 50) || 'No description'}
                        </div>
                        <div className="text-xs text-gray-400">
                          Created: {job.createdAt ? new Date(job.createdAt).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm text-gray-900">
                          <strong>From:</strong> {job.pickupLocation || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-900">
                          <strong>To:</strong> {job.deliveryLocation || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                          {job.status || 'UNKNOWN'}
                        </span>
                        {job.priority && (
                          <div>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(job.priority)}`}>
                              {job.priority}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProgressColor(job.progress)}`}>
                          {job.progress || 'AVAILABLE'}
                        </span>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              job.progress === 'DELIVERED' ? 'bg-green-500 w-full' :
                              job.progress === 'PICKED_UP' ? 'bg-blue-500 w-3/4' :
                              job.progress === 'IN_PROGRESS' ? 'bg-yellow-500 w-1/2' :
                              'bg-gray-400 w-1/4'
                            }`}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm text-gray-900">
                          {job.driverId ? (
                            <span className="text-green-600 font-medium">Assigned</span>
                          ) : (
                            <span className="text-yellow-600 font-medium">Unassigned</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {job.driver?.name || job.driver || 'No driver'}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          ${job.price || job.compensation?.totalPay || 0}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewJob(job)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FaEye className="h-4 w-4" />
                        </button>
                        {job.status === 'AVAILABLE' && (
                          <button 
                            onClick={() => handleOpenAssignModal(job)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Assign Job"
                          >
                            <FaUser className="h-4 w-4" />
                          </button>
                        )}
                        <button 
                          onClick={() => handleEditJob(job)}
                          className="text-green-600 hover:text-green-900"
                          title="Edit Job"
                        >
                          <FaEdit className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteJob(job)}
                          disabled={deletingJob === job.id || !['AVAILABLE', 'CANCELLED'].includes(job.status)}
                          className={`${
                            ['AVAILABLE', 'CANCELLED'].includes(job.status)
                              ? 'text-red-600 hover:text-red-900'
                              : 'text-gray-400 cursor-not-allowed'
                          } disabled:opacity-50 transition-colors`}
                          title={
                            ['AVAILABLE', 'CANCELLED'].includes(job.status)
                              ? 'Delete Job'
                              : 'Cannot delete jobs that are in progress or completed'
                          }
                        >
                          {deletingJob === job.id ? (
                            <FaSpinner className="h-4 w-4 animate-spin" />
                          ) : (
                            <FaTrash className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {jobs.length === 0 && !loading && (
          <div className="text-center py-12">
            <FaTruck className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || priorityFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'No jobs have been created yet. Get started by creating a new job.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Job Form Modal */}
      <JobFormModal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
        onSuccess={() => {
          fetchJobs();
        }}
      />

      {/* Job Details Modal */}
      <JobDetailsModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
      />

      {/* Assign Job Modal */}
      <AssignJobModal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
        availableDrivers={availableDrivers}
        onAssign={handleAssignJob}
      />

      {/* Delete Job Modal */}
      <DeleteJobModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
        onDelete={handleConfirmDelete}
      />
    </div>
  );
};

export default AdminJobs; 